/**
 * TasksRelatedList - Listagem de tarefas vinculadas a um Negócio ou Cliente
 *
 * Componente reaproveitado 100% do layout/UX da TaskSubtasksTab. A diferença é a
 * fonte de dados: chama a RPC get_tasks_paginated com p_deal_id ou p_contact_id.
 *
 * Mantém:
 *  - Mesma tabela, dropdown de fase inline, badges de prazo, status, prioridade
 *  - Permissões granulares por fase (useTaskPhasePermissions por categoria de cada linha)
 *  - Visibilidade server-side via can_user_view_task (na própria RPC)
 *  - Expand de subtarefas (lazy load via get_subtasks_for_list)
 *  - Botão "Nova Tarefa" abre TaskFormDialog com defaultDealId / defaultContactId
 *
 * @version 1.4.0
 * RELEASE: 2026-08-21 v1.4.0 - Invalida também a query dos contadores por tipo
 *          (['tasks-related-type-counts']) usada nos badges das subabas.
 * RELEASE: 2026-08-21 v1.3.0 - Respeita o módulo corrente (normal/retenção/jurídico) via
 *          useTaskModule (com override do TaskModuleProvider) e exibe o Nº de protocolo
 *          na coluna Nº quando a tarefa possuir protocol_number.
 * RELEASE: 2026-08-19 v1.2.0 - No contexto de veículo, o contato deixa de filtrar a listagem
 *          (continua apenas pré-preenchendo o formulário). Corrige divergência entre o contador
 *          da aba e a lista após transferência de posse do veículo.
 * RELEASE: 2026-08-08 v1.1.0 - Suporte a context.vehicleId (aba Tarefas do modal de veículo)
 * RELEASE: 2026-04-19 v1.0.0 - Criação inicial

 */
import { useState, useCallback, Fragment, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTaskModule } from '@/hooks/useTaskModuleType';
import { getTaskModuleMenuKeys } from '@/lib/tasks/taskModuleMenuKeys';
import {
  ListChecks,
  MessageSquare,
  Plus,
  Eye,
  Pencil,
  Filter,
  CheckCircle2,
  PauseCircle,
  XCircle,
  RotateCcw,
  PlayCircle,
  ChevronDown,
  ChevronRight,
  CalendarIcon,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { format, differenceInMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTaskPhasePermissions } from '@/hooks/useTaskPhasePermissions';
import { usePermissions } from '@/hooks/usePermissions';
import { PriorityFlag, PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { TaskFormDialog } from '@/components/tasks/modal/TaskFormDialog';
import { TaskDetailsModal } from '@/components/tasks/modal/TaskDetailsModal';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('TasksRelatedList');

/** Cores da barra lateral de prioridade (idêntico à TaskSubtasksTab) */
const PRIORITY_BAR_COLORS: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  normal: 'bg-blue-500',
  low: 'bg-muted-foreground/30',
};

/** Labels de status (idêntico à TaskSubtasksTab) */
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Andamento',
  paused: 'Pausada',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

/** Estilos de status — cores vibrantes sólidas (incluindo pastel para pausada) */
const STATUS_STYLES: Record<string, string> = {
  pending: 'inline-flex items-center rounded-full font-medium bg-slate-100 text-slate-700 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-slate-500 before:mr-1.5',
  in_progress: 'inline-flex items-center rounded-full font-medium bg-blue-50 text-blue-700 hover:bg-blue-50 dark:bg-blue-950/40 dark:text-blue-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-blue-500 before:mr-1.5',
  paused: 'inline-flex items-center rounded-full font-medium bg-amber-50 text-amber-800 hover:bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-amber-500 before:mr-1.5',
  completed: 'inline-flex items-center rounded-full font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-emerald-500 before:mr-1.5',
  cancelled: 'inline-flex items-center rounded-full font-medium bg-rose-50 text-rose-700 hover:bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-rose-500 before:mr-1.5',
};

/** Item de tarefa retornado pela RPC (subset usado na tabela) */
interface RelatedTaskItem {
  id: string;
  number: number;
  title: string;
  category_id: string;
  phase_id: string;
  parent_task_id: string | null;
  category_name: string;
  category_color: string;
  classification_name: string | null;
  classification_color: string | null;
  phase_name: string;
  phase_color: string;
  priority: string;
  status: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_to_avatar: string | null;
  due_date: string | null;
  created_at: string;
  is_overdue: boolean;
  subtasks_count: number;
  comments_count: number;
  phase_sort_order: number;
  total_category_phases: number;
  /** v1.3.0: protocolo automático (#CAN-/#JUR-) — presente em Retenção/Jurídico */
  protocol_number?: string | null;
}

/** Contexto: deve receber dealId OU contactId (não ambos) */
interface TasksRelatedListProps {
  context: { dealId?: string; contactId?: string | null; vehicleId?: string | null };
  /** v1.2.0: rótulo do veículo do contexto (exibido no chip do formulário de tarefa) */
  vehicleLabel?: string;
  /** Callback opcional ao abrir uma tarefa (não usado quando o modal interno está ativo) */
  onNavigateToTask?: (taskId: string) => void;
  /** Quando true, oculta o título "Tarefas" e exibe apenas botão "+" compacto (uso no sidebar do chat) */
  compactHeader?: boolean;
}

/**
 * PhaseDropdown inline — replica o componente da TaskSubtasksTab
 */
function PhaseDropdown({ task, disabled = false }: { task: RelatedTaskItem; disabled?: boolean }) {
  const queryClient = useQueryClient();
  const [phases, setPhases] = useState<Array<{ id: string; name: string; color: string }>>([]);
  const [loading, setLoading] = useState(false);

  const handleOpenChange = async (open: boolean) => {
    if (!open || !task.category_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_task_phases', { p_category_id: task.category_id });
      if (error) throw error;
      setPhases((data || []) as Array<{ id: string; name: string; color: string }>);
    } catch (err: any) {
      logger.error('Erro ao buscar fases', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePhase = async (phaseId: string) => {
    if (phaseId === task.phase_id) return;
    try {
      const { error } = await supabase.rpc('move_task_phase', { p_task_id: task.id, p_new_phase_id: phaseId });
      if (error) throw error;
      toast.success('Fase alterada com sucesso');
      queryClient.invalidateQueries({ queryKey: ['tasks-related'] });
      // v1.4.0: mantém os badges das subabas (Normal/Retenção/Jurídico) sincronizados
      queryClient.invalidateQueries({ queryKey: ['tasks-related-type-counts'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
    } catch (err: any) {
      logger.error('Erro ao alterar fase', err);
      toast.error(`Erro ao alterar fase: ${err.message}`);
    }
  };

  if (disabled) {
    return (
      <div className="min-w-0 max-w-[140px] px-1 py-0.5">
        <span className="text-xs text-muted-foreground truncate block">{task.phase_name || 'Sem fase'}</span>
        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted/50 mt-0.5">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${task.total_category_phases > 0 ? Math.max(10, (task.phase_sort_order / task.total_category_phases) * 100) : 10}%`,
              backgroundColor: task.phase_color,
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <div className="group/phase cursor-pointer hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 transition-colors min-w-0 max-w-[140px]">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground truncate block">{task.phase_name || 'Sem fase'}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground opacity-0 group-hover/phase:opacity-100 transition-opacity shrink-0" />
          </div>
          <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted/50 mt-0.5">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${task.total_category_phases > 0 ? Math.max(10, (task.phase_sort_order / task.total_category_phases) * 100) : 10}%`,
                backgroundColor: task.phase_color,
              }}
            />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="px-2 py-2 text-xs text-muted-foreground">Carregando...</div>
        ) : phases.length === 0 ? (
          <div className="px-2 py-2 text-xs text-muted-foreground">Nenhuma fase encontrada</div>
        ) : (
          phases.map((phase) => (
            <DropdownMenuItem
              key={phase.id}
              onClick={() => handleChangePhase(phase.id)}
              className={cn('flex items-center gap-2 text-xs', phase.id === task.phase_id && 'bg-accent font-medium')}
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: phase.color }} />
              {phase.name}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Linha de tarefa: usa hook de permissões pela categoria da própria linha
 * (cada linha pode pertencer a uma categoria diferente).
 */
function TaskRow({
  task,
  isSubRow = false,
  onView,
  onEdit,
  onChangeStatus,
  onToggleExpand,
  isExpanded,
  isLoadingExpand,
  showExpandChevron,
  formatDue,
  getDeadlineInfo,
}: {
  task: RelatedTaskItem;
  isSubRow?: boolean;
  onView: (t: RelatedTaskItem) => void;
  onEdit: (t: RelatedTaskItem) => void;
  onChangeStatus: (id: string, newStatus: string) => void;
  onToggleExpand?: (id: string, e: React.MouseEvent) => void;
  isExpanded?: boolean;
  isLoadingExpand?: boolean;
  showExpandChevron?: boolean;
  formatDue: (d: string | null) => string;
  getDeadlineInfo: (d: string | null, s: string) => any;
}) {
  const { user } = useAuth();
  const { canDoOnPhase } = useTaskPhasePermissions(task.category_id);
  const isResponsible = task.assigned_to === user?.id;

  const variantStyles: Record<string, string> = {
    overdue: 'bg-red-500/15 text-red-600 dark:text-red-400',
    warning: 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400',
    remaining: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  };

  return (
    <TableRow className={cn('cursor-pointer hover:bg-muted/40 group', isSubRow && 'bg-muted/20')} onClick={() => onView(task)}>
      <TableCell className={cn('px-2', isSubRow ? 'py-1.5' : 'py-2')}>
        <div className={cn('w-1 rounded-full', isSubRow ? 'h-6' : 'h-8', PRIORITY_BAR_COLORS[task.priority] || 'bg-muted-foreground/30')} />
      </TableCell>
      <TableCell
        className={cn(
          'font-mono text-xs text-muted-foreground',
          isSubRow ? 'py-1.5' : 'py-2',
          showExpandChevron && task.subtasks_count > 0 && 'cursor-pointer select-none hover:bg-muted/40 transition-colors',
        )}
        onClick={showExpandChevron && task.subtasks_count > 0 && onToggleExpand ? (e) => onToggleExpand(task.id, e) : undefined}
      >
        <div className={cn('flex items-center gap-1', isSubRow && 'pl-5')}>
          {isSubRow && <span className="text-muted-foreground/30">└</span>}
          {showExpandChevron && task.subtasks_count > 0 ? (
            <ChevronRight
              className={cn('h-3.5 w-3.5 shrink-0 transition-transform duration-200', isExpanded && 'rotate-90', isLoadingExpand && 'opacity-50')}
            />
          ) : (
            <span className="w-3.5 shrink-0" />
          )}
          {/* v1.3.0: em Retenção/Jurídico o protocolo é o identificador oficial da tarefa */}
          {task.protocol_number ? (
            <span className="truncate" title={task.protocol_number}>{task.protocol_number}</span>
          ) : (
            <>#{task.number}</>
          )}
        </div>
      </TableCell>
      <TableCell className={cn('max-w-[300px]', isSubRow ? 'py-1.5' : 'py-2')}>
        <div className={cn('min-w-0', isSubRow && 'pl-2')}>
          <p className="text-sm font-medium truncate">{task.title}</p>
          {task.classification_name && <p className="text-xs text-muted-foreground truncate">{task.classification_name}</p>}
        </div>
      </TableCell>
      <TableCell className={cn('text-center', isSubRow ? 'py-1.5' : 'py-2')}>
        <div className="flex items-center justify-center gap-2">
          {task.subtasks_count > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <ListChecks className="h-3 w-3" />
              {task.subtasks_count}
            </span>
          )}
          {task.comments_count > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <MessageSquare className="h-3 w-3" />
              {task.comments_count}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className={cn(isSubRow ? 'py-1.5' : 'py-2')}>
        {task.category_name ? (
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: task.category_color || 'hsl(var(--muted-foreground))' }}
            />
            <span className="text-xs text-muted-foreground truncate">{task.category_name}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </TableCell>
      <TableCell className={cn(isSubRow ? 'py-1.5' : 'py-2')}>
        <PhaseDropdown task={task} disabled={!canDoOnPhase(task.phase_id, 'can_change_phase', isResponsible)} />
      </TableCell>
      <TableCell className={cn(isSubRow ? 'py-1.5' : 'py-2')}>
        <div className="flex items-center gap-1.5">
          <PriorityFlag priority={task.priority} size={12} />
          <span className="text-xs text-muted-foreground">{PRIORITY_LABELS[task.priority] || task.priority}</span>
        </div>
      </TableCell>
      <TableCell className={cn(isSubRow ? 'py-1.5' : 'py-2')}>
        <Badge className={cn('text-[10px] h-5 px-1.5 whitespace-nowrap', STATUS_STYLES[task.status])}>
          {STATUS_LABELS[task.status] || task.status}
        </Badge>
      </TableCell>
      <TableCell className={cn(isSubRow ? 'py-1.5' : 'py-2')}>
        {task.assigned_to_name ? (
          <div className="flex items-center gap-2">
            <SmartAvatar src={task.assigned_to_avatar} name={task.assigned_to_name} size="xs" use3DFallback />
            <span className="text-xs text-muted-foreground truncate max-w-[80px]">{task.assigned_to_name}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </TableCell>
      <TableCell className={cn(isSubRow ? 'py-1.5' : 'py-2')}>
        {task.due_date ? (() => {
          const deadline = getDeadlineInfo(task.due_date, task.status);
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <CalendarIcon className="w-3.5 h-3.5 shrink-0" />
                {formatDue(task.due_date)}
              </div>
              {deadline && (
                <div className={cn('inline-flex items-center gap-0.5 px-1 py-px rounded-full text-[10px] font-bold uppercase w-fit', variantStyles[deadline.variant])}>
                  {deadline.isOverdue ? <AlertTriangle className="w-3 h-3 shrink-0" /> : <Clock className="w-3 h-3 shrink-0" />}
                  {deadline.label}
                </div>
              )}
            </div>
          );
        })() : (
          <span className="text-xs text-muted-foreground/50">—</span>
        )}
      </TableCell>
      <TableCell className={cn(isSubRow ? 'py-1.5' : 'py-2')} onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
              <Filter className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 p-1.5 shadow-lg border-border/50">
            <DropdownMenuItem onClick={() => onView(task)} className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Ver Detalhes</span>
            </DropdownMenuItem>

            {canDoOnPhase(task.phase_id, 'can_edit', isResponsible) && (
              <DropdownMenuItem onClick={() => onEdit(task)} className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer">
                <Pencil className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Editar</span>
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator className="my-1.5" />

            {task.status === 'in_progress' && canDoOnPhase(task.phase_id, 'can_change_status', isResponsible) && (
              <>
                <DropdownMenuItem onClick={() => onChangeStatus(task.id, 'paused')} className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-yellow-600 dark:text-yellow-400 focus:text-yellow-600 focus:bg-yellow-50 dark:focus:bg-yellow-950/30">
                  <PauseCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Pausar</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onChangeStatus(task.id, 'completed')} className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-emerald-600 dark:text-emerald-400 focus:text-emerald-600 focus:bg-emerald-50 dark:focus:bg-emerald-950/30">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm font-medium">Concluir</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onChangeStatus(task.id, 'cancelled')} className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 mt-1">
                  <XCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Cancelar</span>
                </DropdownMenuItem>
              </>
            )}

            {task.status === 'paused' && canDoOnPhase(task.phase_id, 'can_change_status', isResponsible) && (
              <>
                <DropdownMenuItem onClick={() => onChangeStatus(task.id, 'in_progress')} className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-blue-600 dark:text-blue-400 focus:text-blue-600 focus:bg-blue-50 dark:focus:bg-blue-950/30">
                  <PlayCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Retomar</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onChangeStatus(task.id, 'cancelled')} className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 mt-1">
                  <XCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Cancelar</span>
                </DropdownMenuItem>
              </>
            )}

            {(task.status === 'completed' || task.status === 'cancelled') && canDoOnPhase(task.phase_id, 'can_reopen', isResponsible) && (
              <DropdownMenuItem onClick={() => onChangeStatus(task.id, 'in_progress')} className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer">
                <RotateCcw className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Reabrir Tarefa</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

/**
 * Listagem reaproveitável de tarefas vinculadas a um Negócio ou Cliente.
 */
export function TasksRelatedList({ context, vehicleLabel, onNavigateToTask, compactHeader = false }: TasksRelatedListProps) {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const { canCreate } = usePermissions();
  // v1.3.0: módulo corrente (rota ou override do TaskModuleProvider nas subabas dos modais)
  const taskModule = useTaskModule();
  const usesProtocol = taskModule.type !== 'normal';
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  // Modal interno para visualizar a tarefa quando o componente é usado em outro modal
  const [viewingTaskId, setViewingTaskId] = useState<string | null>(null);

  // Estados de expand de subtarefas (lazy load)
  const [expandedSubs, setExpandedSubs] = useState<Set<string>>(new Set());
  const [subSubtasksMap, setSubSubtasksMap] = useState<Record<string, RelatedTaskItem[]>>({});
  const [loadingSubSubs, setLoadingSubSubs] = useState<Set<string>>(new Set());

  // Permissão de criação de tarefa - usa helper canCreate() do PermissionsContext
  // que já trata bypass de root_admin/tenant_admin e valida supervisor pelo perfil
  const canCreateTask = useMemo(
    // 2026-10-22: chaves do módulo da subaba (Retenção/Jurídico separados de Tarefas)
    () => {
      const k = getTaskModuleMenuKeys(taskModule.type);
      return canCreate(k.list) || canCreate(k.dashboard);
    },
    [canCreate, taskModule.type]
  );

  const dealId = context.dealId || null;
  const contactId = context.contactId || null;
  // v1.1.0: contexto de veículo (aba Tarefas do modal de veículo)
  const vehicleId = context.vehicleId || null;
  const hasContext = !!(dealId || contactId || vehicleId);

  /**
   * v1.2.0: no contexto de VEÍCULO o contato serve apenas para pré-preencher o
   * formulário de nova tarefa — NUNCA para filtrar a listagem.
   *
   * Motivo: após uma transferência de posse, o contato atual do veículo passa a ser
   * diferente do contato gravado nas tarefas antigas. Filtrar por veículo E contato
   * (AND na RPC) descartava essas tarefas, gerando divergência com o contador da aba
   * (get_vehicle_details), que conta apenas pelo vínculo com o veículo.
   */
  const filterContactId = vehicleId ? null : contactId;

  // Busca tarefas via RPC compartilhada (get_tasks_paginated v1.18.0)
  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['tasks-related', currentTenant?.id, dealId, filterContactId, vehicleId, taskModule.type],
    queryFn: async () => {
      if (!hasContext) return [] as RelatedTaskItem[];

      const { data, error } = await supabase.rpc('get_tasks_paginated', {
        p_page: 1,
        p_per_page: 100,
        p_sort_by: 'created_at',
        p_sort_dir: 'desc',
        p_hide_completed: false,
        p_tenant_hint: currentTenant?.id || null,
        p_deal_id: dealId,
        p_contact_id: filterContactId,
        p_vehicle_id: vehicleId,
        // v1.3.0: sem isto a RPC assume 'normal' e oculta tarefas de Retenção/Jurídico
        p_task_type: taskModule.type,
      } as any);


      if (error) {
        logger.error('Erro ao buscar tarefas relacionadas', error);
        throw error;
      }
      return (data || []) as RelatedTaskItem[];
    },
    enabled: !!currentTenant?.id && hasContext,
    staleTime: 30_000,
  });

  /** Abre o modal interno (ou navega via callback se fornecido) */
  const handleView = useCallback((t: RelatedTaskItem) => {
    if (onNavigateToTask) {
      onNavigateToTask(t.id);
      return;
    }
    setViewingTaskId(t.id);
  }, [onNavigateToTask]);

  const handleEdit = useCallback((t: RelatedTaskItem) => {
    setEditingTaskId(t.id);
    setShowEditDialog(true);
  }, []);

  /** Altera status de uma tarefa via RPC */
  const handleChangeStatus = useCallback(async (taskId: string, newStatus: string) => {
    try {
      const { error } = await supabase.rpc('change_task_status', {
        p_task_id: taskId,
        p_new_status: newStatus as any,
      });
      if (error) throw error;
      toast.success(`Status alterado para "${STATUS_LABELS[newStatus] || newStatus}"`);
      queryClient.invalidateQueries({ queryKey: ['tasks-related'] });
      // v1.4.0: mantém os badges das subabas (Normal/Retenção/Jurídico) sincronizados
      queryClient.invalidateQueries({ queryKey: ['tasks-related-type-counts'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['deal-modal-data'] });
      queryClient.invalidateQueries({ queryKey: ['customer-modal-data'] });
    } catch (err: any) {
      logger.error('Erro ao alterar status', err);
      toast.error(err.message || 'Erro ao alterar status');
    }
  }, [queryClient]);

  /** Expand/colapsa subtarefas (nível 2) — usa get_subtasks_for_list */
  const toggleExpandSub = useCallback(async (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (expandedSubs.has(taskId)) {
      setExpandedSubs((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      return;
    }
    if (!subSubtasksMap[taskId]) {
      setLoadingSubSubs((prev) => new Set(prev).add(taskId));
      try {
        const { data, error } = await supabase.rpc('get_subtasks_for_list', {
          p_task_id: taskId,
          p_tenant_hint: null,
        });
        if (error) throw error;
        setSubSubtasksMap((prev) => ({ ...prev, [taskId]: (data || []) as RelatedTaskItem[] }));
      } catch (err: any) {
        logger.error('Erro ao buscar subtarefas', err);
        toast.error('Erro ao carregar subtarefas');
        return;
      } finally {
        setLoadingSubSubs((prev) => {
          const next = new Set(prev);
          next.delete(taskId);
          return next;
        });
      }
    }
    setExpandedSubs((prev) => new Set(prev).add(taskId));
  }, [expandedSubs, subSubtasksMap]);

  const formatDue = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    return format(new Date(dateStr), 'dd/MM/yy', { locale: ptBR });
  };

  /** Calcula info de prazo (overdue/warning/remaining) */
  const getDeadlineInfo = (dueDate: string | null, status: string) => {
    if (!dueDate) return null;
    if (status === 'completed' || status === 'cancelled') return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diffMin = differenceInMinutes(due, now);
    if (diffMin < 0) {
      const absDiff = Math.abs(diffMin);
      const days = Math.floor(absDiff / (60 * 24));
      const hours = Math.floor((absDiff % (60 * 24)) / 60);
      return { label: days > 0 ? `${days}d ${hours}h` : `${hours}h`, variant: 'overdue' as const, isOverdue: true };
    }
    if (diffMin < 60 * 24) {
      const hours = Math.floor(diffMin / 60);
      const mins = diffMin % 60;
      return { label: hours > 0 ? `${hours}h ${mins}m` : `${mins}m`, variant: 'warning' as const, isOverdue: false };
    }
    const days = Math.floor(diffMin / (60 * 24));
    const hours = Math.floor((diffMin % (60 * 24)) / 60);
    return { label: `${days}d ${hours}h`, variant: 'remaining' as const, isOverdue: false };
  };

  // Loading
  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-8 w-32" />
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  // Empty
  if (!hasContext || tasks.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="p-3 rounded-full bg-muted mb-3">
            <ListChecks className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-sm font-medium mb-1">Nenhuma tarefa vinculada</h3>
          <p className="text-xs text-muted-foreground max-w-xs mb-4">
            Crie tarefas relacionadas para acompanhar atividades e responsabilidades.
          </p>
          {canCreateTask && hasContext && (
            <Button variant="outline" size="sm" onClick={() => setShowCreateDialog(true)} className="text-xs gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Criar primeira tarefa
            </Button>
          )}
        </div>

        {showCreateDialog && (
        <TaskFormDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          defaultDealId={dealId || undefined}
          defaultContactId={contactId || undefined}
          defaultVehicleId={vehicleId || undefined}
          defaultVehicleLabel={vehicleLabel}
          onSuccess={() => {
            setShowCreateDialog(false);
            queryClient.invalidateQueries({ queryKey: ['tasks-related'] });
      // v1.4.0: mantém os badges das subabas (Normal/Retenção/Jurídico) sincronizados
      queryClient.invalidateQueries({ queryKey: ['tasks-related-type-counts'] });
            queryClient.invalidateQueries({ queryKey: ['deal-modal-data'] });
            queryClient.invalidateQueries({ queryKey: ['customer-modal-data'] });
          }}
        />
        )}
      </>
    );
  }

  return (
    <div className="space-y-3 w-full min-w-0">
      {/* Header: modo padrão exibe título + contagem; modo compacto (sidebar do chat) exibe apenas botão "+" alinhado à direita */}
      {compactHeader ? (
        canCreateTask && (
          <div className="flex w-full min-w-0 items-center justify-end flex-shrink-0 pr-1">
            <Button size="icon" variant="outline" onClick={() => setShowCreateDialog(true)} className="h-7 w-7" title="Nova Tarefa">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        )
      ) : (
        <div className="flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">Tarefas</h3>
            <Badge variant="secondary" className="text-xs">{tasks.length}</Badge>
          </div>
          {canCreateTask && (
            <Button size="sm" onClick={() => setShowCreateDialog(true)} className="h-8 text-xs gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Nova Tarefa
            </Button>
          )}
        </div>
      )}

      {/* Wrapper com scroll horizontal: garante navegação nas colunas em containers estreitos (ex: sidebar do chat) */}
      {/* v1.1.0: header com bg-muted/20 e borders/40 para paridade visual com CustomerDealsTab */}
      <div className="border border-border/50 rounded-lg overflow-x-auto max-w-full">
        <Table className="min-w-[760px]">
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
              <TableHead className="w-6" />
              <TableHead className={cn('text-xs font-medium py-2', usesProtocol ? 'w-40' : 'w-16')}>
                {usesProtocol ? 'Protocolo' : 'Nº'}
              </TableHead>
              <TableHead className="text-xs font-medium py-2">Título</TableHead>
              <TableHead className="w-16 text-center text-xs font-medium py-2">Info</TableHead>
              <TableHead className="w-32 text-xs font-medium py-2">Categoria</TableHead>
              <TableHead className="w-28 text-xs font-medium py-2">Fase</TableHead>
              <TableHead className="w-24 text-xs font-medium py-2">Prioridade</TableHead>
              <TableHead className="w-28 text-xs font-medium py-2">Status</TableHead>
              <TableHead className="w-36 text-xs font-medium py-2">Responsável</TableHead>
              <TableHead className="w-32 text-xs font-medium py-2">Prazo</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.map((task) => (
              <Fragment key={`row-${task.id}`}>
                <TaskRow
                  task={task}
                  onView={handleView}
                  onEdit={handleEdit}
                  onChangeStatus={handleChangeStatus}
                  onToggleExpand={toggleExpandSub}
                  isExpanded={expandedSubs.has(task.id)}
                  isLoadingExpand={loadingSubSubs.has(task.id)}
                  showExpandChevron
                  formatDue={formatDue}
                  getDeadlineInfo={getDeadlineInfo}
                />

                {expandedSubs.has(task.id) && subSubtasksMap[task.id]?.map((sub) => (
                  <TaskRow
                    key={sub.id}
                    task={sub}
                    isSubRow
                    onView={handleView}
                    onEdit={handleEdit}
                    onChangeStatus={handleChangeStatus}
                    formatDue={formatDue}
                    getDeadlineInfo={getDeadlineInfo}
                  />
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog de criação — pré-preenche Negócio, Contato e Veículo do contexto */}
      {showCreateDialog && (
      <TaskFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        defaultDealId={dealId || undefined}
        defaultContactId={contactId || undefined}
        defaultVehicleId={vehicleId || undefined}
        defaultVehicleLabel={vehicleLabel}
        onSuccess={() => {
          setShowCreateDialog(false);
          queryClient.invalidateQueries({ queryKey: ['tasks-related'] });
      // v1.4.0: mantém os badges das subabas (Normal/Retenção/Jurídico) sincronizados
      queryClient.invalidateQueries({ queryKey: ['tasks-related-type-counts'] });
          queryClient.invalidateQueries({ queryKey: ['deal-modal-data'] });
          queryClient.invalidateQueries({ queryKey: ['customer-modal-data'] });
        }}
      />
      )}

      {/* Dialog de edição */}
      {editingTaskId && (
        <TaskFormDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          editTaskId={editingTaskId}
          onSuccess={() => {
            setShowEditDialog(false);
            setEditingTaskId(null);
            queryClient.invalidateQueries({ queryKey: ['tasks-related'] });
      // v1.4.0: mantém os badges das subabas (Normal/Retenção/Jurídico) sincronizados
      queryClient.invalidateQueries({ queryKey: ['tasks-related-type-counts'] });
          }}
        />
      )}

      {/* Modal interno de detalhes da tarefa (quando não há onNavigateToTask) */}
      {!onNavigateToTask && viewingTaskId && (
        <TaskDetailsModal
          taskId={viewingTaskId}
          open={!!viewingTaskId}
          onOpenChange={(open) => {
            if (!open) setViewingTaskId(null);
          }}
        />
      )}
    </div>
  );
}
